import React, { useState, useEffect, useContext, useRef } from "react";
import { ReactReduxContext, useSelector } from "react-redux";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import TextfieldInput from "@_components/TextfieldInput/TextfieldInput";
import Button from "@mui/material/Button";
import styles from "./FindingsPopup.module.css";
import { useDispatch } from "react-redux";
import { caseActions } from "@_actions/case.actions";
import { casesActions } from "@_actions/cases.actions";
import { catchError, isComponentDisabled } from "@_utils/helpers";
import { LicenseConstants } from "@_constants/license.constants";
import UploadLicensePopup from "../UploadLicensePopup/UploadLicensePopup";
import { caseConstants } from "@_constants/case.constants";
import { Tooltip, CircularProgress } from "@mui/material";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";
import Scrollbar from "react-perfect-scrollbar-z";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import "scrollbar.css";
import { caseActivityActions } from "@_actions/caseActivity.actions";
import AreYouSurePopup from "../AreYouSurePopup/AreYouSurePopup";
import { validateLink, getBase64 } from "@_utils/helpers";
import makeStyles from "@mui/styles/makeStyles";

const componetTag = "finding_popup";
function FindingsPopup(props) {
  const { store } = useContext(ReactReduxContext);
  const { findingsData, caseId, overviewData, data } = props;
  const inputDisabled = isComponentDisabled(componetTag, data.state);
  const dispatch = useDispatch();
  const caseReducer = useSelector((state) => state.caseReducer);
  const useStyles = makeStyles({
    helperText: { fontSize: "12px !important" },
  });
  const classes = useStyles();

  const [selectedFinding, setSelectedFinding] = useState({});
  const [uploadPopup, setUploadPopup] = useState(false);
  const [openAreYouSurePopup, setOpenAreYouSurePopup] = useState(false);
  const [areYouSurePopupText, setAreYouSurePopupText] = useState("");
  const [areYouSurePopupAction, setAreYouSurePopupAction] = useState(null);
  const [editingPageLinkIndex, setEditingPageLinkIndex] = useState(null);
  const [editedPageLink, setEditedPageLink] = useState("");
  const [pageLinkError, setPageLinkError] = useState("");
  const [localFindingsData, setLocalFindingsData] = useState([]);
  const [isClickingEditIcon, setIsClickingEditIcon] = useState(false);
  const [selectedScreenshotFile, setSelectedScreenshotFile] = useState({
    file: null,
    image_data: "",
    image_name: "",
    findingId: null,
  });
  const [uploadingScreenshotFindingId, setUploadingScreenshotFindingId] =
    useState(null);
  const { snackbarProps, showSnackbarError } = useSnackbar();
  const screenshotImageRefs = useRef({});
  const pageLinkInputRef = useRef(null);

  const setScreenshotImageRef = (element, findingId) => {
    if (element) {
      screenshotImageRefs.current[findingId] = element;
    }
  };

  const forceReloadScreenshotImage = (findingId) => {
    const imageElement = screenshotImageRefs.current[findingId];
    if (!imageElement) return;

    const currentSrc = imageElement.src;
    const baseUrl = currentSrc.split("?")[0];
    const cacheBustingUrl = `${baseUrl}?t=${Date.now()}`;
    imageElement.src = cacheBustingUrl;
  };

  const resetPageLinkEditingState = () => {
    setEditingPageLinkIndex(null);
    setEditedPageLink("");
    setPageLinkError("");
  };

  useEffect(() => {
    if (findingsData && findingsData.length > 0) {
      setLocalFindingsData(findingsData);
    }
    resetPageLinkEditingState();
  }, [findingsData]);

  const closePopup = () => {
    resetPageLinkEditingState();
    props.close();
  };

  const handleDeleteFindingClick = (index) => {
    setAreYouSurePopupText("Do you really want to delete this finding?");
    setAreYouSurePopupAction(() => () => {
      setOpenAreYouSurePopup(false);
      deleteFinding(index);
    });
    setOpenAreYouSurePopup(true);
  };

  const handleDeleteFindingIconClick = (e, index) => {
    if (
      inputDisabled ||
      (findingsData.length === 1 &&
        data.state === caseConstants.CASE_STATE_ACTIVE)
    ) {
      return;
    }
    e.preventDefault();
    handleDeleteFindingClick(index);
  };

  const handleDeleteLicenseClick = (index, findingId) => {
    setAreYouSurePopupText("Do you really want to delete this license?");
    setAreYouSurePopupAction(() => () => {
      setOpenAreYouSurePopup(false);
      deleteLicense(index, findingId);
    });
    setOpenAreYouSurePopup(true);
  };

  function deleteFinding(index) {
    resetPageLinkEditingState();
    dispatch(caseActions.deleteCaseFinding(caseId, findingsData[index].id));
    setTimeout(() => {
      dispatch(casesActions.getCases());
      setTimeout(() => {
        dispatch(caseActions.getCaseWithChargeDetails(caseId, overviewData));
        // TODO: snackbar and error handling before closing the popup
        setTimeout(() => {
          const currCase = store.getState().caseReducer.case[caseId];
          if (currCase.findings.length === 0) props.close();
        }, 150);
      }, 150);
    }, 150);
  }

  const deleteLicense = (index, findingId) => {
    dispatch(caseActions.deleteCaseLicense(data.id, index, findingId));
  };

  useEffect(() => {
    if (caseReducer.status["CASE_DELETE_LICENSE"] === "success") {
      dispatch(caseActions.clearStateMessage("CASE_DELETE_LICENSE"));
      dispatch(casesActions.getCases());
      dispatch(casesActions.getCasesCount());
      dispatch(caseActions.getCaseWithChargeDetails(caseId, data.overview));
      dispatch(caseActivityActions.getCaseActivities(caseId));
    } else if (catchError(caseReducer.status["CASE_DELETE_LICENSE"])) {
      dispatch(caseActions.clearStateMessage("CASE_DELETE_LICENSE"));
      showSnackbarError("Delete license(s) failed.");
    }
  }, [dispatch, caseId, caseReducer.status, data.overview, showSnackbarError]);

  const handleUploadPopup = (finding) => {
    if (!uploadPopup) setSelectedFinding(finding);
    setUploadPopup(!uploadPopup);
  };

  // Handle file input change
  const handleScreenshotFileChange = async (e, findingId) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    try {
      const base64Result = await getBase64(file);
      setUploadingScreenshotFindingId(findingId);
      setSelectedScreenshotFile({
        file,
        image_data: base64Result,
        image_name: file.name,
        findingId,
      });
    } catch (error) {
      console.error("Error converting file to base64:", error);
      showSnackbarError("Failed to process image.");
    }
  };

  // Upload file when selectedScreenshotFile is set
  useEffect(() => {
    if (
      !selectedScreenshotFile.image_data ||
      !selectedScreenshotFile.image_name ||
      !selectedScreenshotFile.findingId
    ) {
      return;
    }

    const uploadFile = () => {
      try {
        const { image_data, image_name, findingId } = selectedScreenshotFile;
        const base64Data = image_data.split(";base64,")[1];

        if (!base64Data) {
          showSnackbarError("Invalid image data.");
          return;
        }

        const data = {
          screenshot_image_data: base64Data,
          screenshot_name: image_name,
        };

        dispatch(
          caseActions.updateCaseFindingPageScreenshot(data, findingId, caseId),
        );

        setSelectedScreenshotFile({
          file: null,
          image_data: "",
          image_name: "",
          findingId: null,
        });
      } catch (error) {
        console.error("Upload error:", error);
        showSnackbarError("Failed to upload screenshot.");
      }
    };

    uploadFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedScreenshotFile.image_data,
    selectedScreenshotFile.image_name,
    selectedScreenshotFile.findingId,
  ]);

  const handleEditPageLink = (index, currentPageLink) => {
    setEditingPageLinkIndex(index);
    setEditedPageLink(currentPageLink || "");
    setPageLinkError("");
  };

  const handleCancelEditPageLink = () => {
    resetPageLinkEditingState();
  };

  const handlePageLinkInputBlur = (index) => {
    if (!isClickingEditIcon && editingPageLinkIndex === index) {
      handleSavePageLink(index, findingsData[index].id);
    }
    setIsClickingEditIcon(false);
  };

  const handlePageLinkInputKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSavePageLink(index, findingsData[index].id);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      handleCancelEditPageLink();
    }
  };

  const handleSavePageLink = (index, findingId) => {
    const newPageLink = editedPageLink.trim();

    if (!newPageLink) {
      setPageLinkError("Page link is required");
      return;
    }

    if (!validateLink(newPageLink)) {
      setPageLinkError("Please enter a valid page link");
      return;
    }

    setPageLinkError("");
    const data = {
      id: findingId,
      page_link: newPageLink,
    };

    // Optimistically update local state
    const updatedFindings = [...localFindingsData];
    if (updatedFindings[index]) {
      updatedFindings[index] = {
        ...updatedFindings[index],
        page_link: newPageLink,
      };
      setLocalFindingsData(updatedFindings);
    }

    dispatch(caseActions.updateCaseFindingPageLink(caseId, index, data));
    resetPageLinkEditingState();
  };

  useEffect(() => {
    if (caseReducer.status["CASE_UPDATE_FINDING_PAGE_LINK"] === "success") {
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_FINDING_PAGE_LINK"));
      dispatch(caseActions.getCaseWithChargeDetails(caseId, overviewData));
    } else if (
      catchError(caseReducer.status["CASE_UPDATE_FINDING_PAGE_LINK"])
    ) {
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_FINDING_PAGE_LINK"));
      showSnackbarError("Update page link failed.");
    }
  }, [dispatch, caseId, caseReducer.status, overviewData, showSnackbarError]);

  useEffect(() => {
    if (
      caseReducer.status["CASE_UPDATE_FINDING_PAGE_SCREENSHOT"] === "success"
    ) {
      const findingId = uploadingScreenshotFindingId;
      dispatch(
        caseActions.clearStateMessage("CASE_UPDATE_FINDING_PAGE_SCREENSHOT"),
      );
      setUploadingScreenshotFindingId(null);

      if (findingId) {
        forceReloadScreenshotImage(findingId);
      }
    } else if (
      catchError(caseReducer.status["CASE_UPDATE_FINDING_PAGE_SCREENSHOT"])
    ) {
      dispatch(
        caseActions.clearStateMessage("CASE_UPDATE_FINDING_PAGE_SCREENSHOT"),
      );
      setUploadingScreenshotFindingId(null);
      showSnackbarError("Update page screenshot failed.");
    }
  }, [
    dispatch,
    caseReducer.status,
    uploadingScreenshotFindingId,
    showSnackbarError,
  ]);

  return (
    <DialogPopup title="Image uses" open={props.open} onClose={closePopup}>
      <div>
        <Scrollbar maxHeight="74vh">
          <div className={styles.tabs_content_container}>
            <div className={styles.tab_content}>
              <div className={styles.add_finding_container}></div>

              {localFindingsData &&
                localFindingsData.map((finding, index) => (
                  <div key={index} className={styles.finding_container}>
                    <div className={styles.finding_images_container}>
                      <div className={styles.image_container}>
                        <div className={styles.image_wrapper}>
                          <span className={styles.image_label}>
                            Original image
                          </span>
                          <img
                            className={styles.finding_image}
                            src={finding.original_image.original_image_url}
                            alt={""}
                            onClick={() =>
                              window.open(
                                finding.original_image.original_image_url,
                                "_blank",
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className={styles.image_container}>
                        <div className={styles.screenshot_box}>
                          <span className={styles.image_label}>
                            Page screenshot
                          </span>
                          <div className={styles.screenshot_image_wrapper}>
                            {!inputDisabled && (
                              <>
                                <div className={styles.screenshot_edit}>
                                  <Tooltip
                                    title="Change screenshot"
                                    placement="top"
                                  >
                                    <label
                                      htmlFor={`screenshot-input-${index}`}
                                      className={`${styles.edit_icon_overlay} ${
                                        uploadingScreenshotFindingId ===
                                        finding.id
                                          ? styles.edit_icon_overlay_disabled
                                          : ""
                                      }`}
                                    >
                                      <EditIcon />
                                    </label>
                                  </Tooltip>
                                </div>
                                <input
                                  type="file"
                                  id={`screenshot-input-${index}`}
                                  accept=".jpg, .jpeg, .png, .gif"
                                  style={{ display: "none" }}
                                  onChange={(e) =>
                                    handleScreenshotFileChange(e, finding.id)
                                  }
                                />
                              </>
                            )}
                            {uploadingScreenshotFindingId === finding.id && (
                              <div
                                className={styles.screenshot_loading_overlay}
                              >
                                <CircularProgress color="primary" />
                              </div>
                            )}
                            <img
                              ref={(el) =>
                                setScreenshotImageRef(el, finding.id)
                              }
                              className={styles.finding_image}
                              src={finding.page_screenshot_url}
                              alt={""}
                              onClick={() =>
                                window.open(
                                  finding.page_screenshot_url,
                                  "_blank",
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.finding_info_container}>
                      <div className={styles.info_row}>
                        <span className={styles.info_label}>
                          Original image:
                        </span>
                        <span className={styles.info_label_value}>
                          {finding.original_image.filename}
                        </span>
                      </div>
                      <div className={styles.info_row}>
                        {editingPageLinkIndex === index ? (
                          <TextfieldInput
                            className={styles.page_link_input_small}
                            inputRef={pageLinkInputRef}
                            label="Page link"
                            value={editedPageLink}
                            onChange={(e) => setEditedPageLink(e.target.value)}
                            onKeyDown={(e) =>
                              handlePageLinkInputKeyDown(e, index)
                            }
                            onBlur={() => handlePageLinkInputBlur(index)}
                            error={!!pageLinkError}
                            helperText={pageLinkError}
                            FormHelperTextProps={{
                              className: classes.helperText,
                            }}
                            margin="none"
                          />
                        ) : (
                          <>
                            <span className={styles.info_label}>
                              Page link:
                            </span>
                            <div className={styles.page_link_container}>
                              <a
                                className={styles.info_value_link}
                                href={finding.page_link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {finding.page_link}
                              </a>
                              {!inputDisabled && (
                                <EditIcon
                                  className={styles.edit_icon_inline}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setIsClickingEditIcon(true);
                                  }}
                                  onClick={() => {
                                    handleEditPageLink(
                                      index,
                                      finding.page_link,
                                    );
                                    setIsClickingEditIcon(false);
                                  }}
                                />
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      <div className={styles.info_row}>
                        <span className={styles.info_label}>License: </span>
                        <div className={styles.license_container}>
                          {finding.license &&
                          finding.license.id &&
                          (finding.license.license_url ||
                            finding.license.note) ? (
                            <>
                              {/* License row: File link or "no license file uploaded" + status + delete icon */}
                              <div className={styles.license_row}>
                                <div className={styles.license_content}>
                                  {finding.license.license_url ? (
                                    <a
                                      className={styles.info_value_link}
                                      href={finding.license.license_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {finding.license.filename}
                                    </a>
                                  ) : (
                                    <span className={styles.info_value}>
                                      {"<no license file uploaded>"}
                                    </span>
                                  )}
                                </div>
                                <div className={styles.license_actions}>
                                  <span className={styles.license_status}>
                                    {" "}
                                    (
                                    {finding.license.state ===
                                    LicenseConstants.STATE_VALID
                                      ? "Valid"
                                      : finding.license.state ===
                                          LicenseConstants.STATE_INVALID
                                        ? "Invalid"
                                        : "Unchecked"}
                                    )
                                  </span>
                                  {!inputDisabled &&
                                    finding.license &&
                                    finding.license.id && (
                                      <DeleteIcon
                                        className={styles.delete_icon_inline}
                                        onClick={() =>
                                          handleDeleteLicenseClick(
                                            index,
                                            finding.id,
                                          )
                                        }
                                      />
                                    )}
                                </div>
                              </div>
                              {finding.license.note && (
                                <span className={styles.info_value}>
                                  {finding.license.note}
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              {!inputDisabled ? (
                                <Button
                                  className={styles.btn_confirm_small}
                                  size="small"
                                  onClick={() => handleUploadPopup(finding)}
                                >
                                  Add License
                                </Button>
                              ) : (
                                <span className={styles.info_value}>
                                  No license
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`action_container ${styles.finding_action_container}`}
                    >
                      {!inputDisabled && (
                        <>
                          <Tooltip
                            title={
                              inputDisabled ||
                              (findingsData.length === 1 &&
                                data.state === caseConstants.CASE_STATE_ACTIVE)
                                ? "Cannot delete the last finding of an active case"
                                : ""
                            }
                            placement="top"
                            arrow
                          >
                            <span>
                              <DeleteIcon
                                className={`${styles.delete_icon} ${
                                  inputDisabled ||
                                  (findingsData.length === 1 &&
                                    data.state ===
                                      caseConstants.CASE_STATE_ACTIVE)
                                    ? styles.delete_icon_disabled
                                    : ""
                                }`}
                                onClick={(e) =>
                                  handleDeleteFindingIconClick(e, index)
                                }
                              />
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </div>

                    <UploadLicensePopup
                      open={uploadPopup}
                      close={handleUploadPopup}
                      findingId={selectedFinding?.id}
                      caseId={caseId}
                      overviewData={overviewData}
                    />
                  </div>
                ))}
            </div>
          </div>
        </Scrollbar>
      </div>

      <div className="action_container">
        <Button className="btn_cancel" onClick={closePopup}>
          Close
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <AreYouSurePopup
        open={openAreYouSurePopup}
        close={() => {
          setOpenAreYouSurePopup(false);
          setAreYouSurePopupText("");
          setAreYouSurePopupAction(null);
        }}
        onClick={areYouSurePopupAction}
        title="Confirmation required"
      >
        {areYouSurePopupText}
      </AreYouSurePopup>

      <AppSnackbar {...snackbarProps} />
    </DialogPopup>
  );
}

export default FindingsPopup;
